import { useState, useRef, useEffect } from 'react';
import { Form, Button, Card, Container, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/auth.service';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import PageMeta from '../components/common/PageMeta';

// 利用可能な言語リスト（翻訳キーを使用）
const getLanguages = (t: (key: string) => string) => [
  { code: 'ja', name: t('common.language_ja'), nativeName: '日本語' },
  { code: 'en', name: t('common.language_en'), nativeName: 'English' },
  { code: 'zh-CN', name: t('common.language_zh_cn'), nativeName: '中文（简体）' },
  { code: 'fr', name: t('common.language_fr'), nativeName: 'Français' },
  { code: 'es', name: t('common.language_es'), nativeName: 'Español' },
  { code: 'de', name: t('common.language_de'), nativeName: 'Deutsch' },
  { code: 'pt', name: t('common.language_pt'), nativeName: 'Português' },
  { code: 'ru', name: t('common.language_ru'), nativeName: 'Русский' },
];

// 主要なタイムゾーンリスト（ラベルは翻訳キーを使用）
const timezoneKeys = [
  { value: 'Asia/Tokyo', key: 'register.timezone_asia_tokyo' },
  { value: 'UTC', key: 'register.timezone_utc' },
  { value: 'America/New_York', key: 'register.timezone_america_new_york' },
  { value: 'America/Los_Angeles', key: 'register.timezone_america_los_angeles' },
  { value: 'Europe/London', key: 'register.timezone_europe_london' },
  { value: 'Europe/Paris', key: 'register.timezone_europe_paris' },
  { value: 'Asia/Shanghai', key: 'register.timezone_asia_shanghai' },
  { value: 'Asia/Seoul', key: 'register.timezone_asia_seoul' },
  { value: 'Australia/Sydney', key: 'register.timezone_australia_sydney' },
];

const registerDefaults: Record<string, string> = {
  'register.header_title': '新規登録',
  'register.header_subtitle': 'Kensukeアカウントを作成して、サンプル管理を始めましょう',
  'register.basic_info': '基本情報',
  'register.last_name_label': '姓',
  'register.first_name_label': '名',
  'register.last_name_placeholder': '例: 山田',
  'register.first_name_placeholder': '例: 太郎',
  'register.email_label': 'メールアドレス',
  'register.email_login_note':
    'ログインに使用します。メールアドレスはユーザー名としても使用されます。',
  'register.username_label': 'ユーザー名（省略可）',
  'register.username_placeholder': '未入力の場合はメールアドレスを使用します',
  'register.username_note':
    '※ ログインには使用されません。システム内での表示名として使用されます（未入力の場合はメールアドレスが表示名になります）',
  'register.organization_section': '組織情報（任意）',
  'register.organization_name': '組織名',
  'register.organization_name_placeholder': '例: 株式会社サンプル',
  'register.organization_name_note': '所属する組織名を入力してください',
  'register.department_name': '部署名',
  'register.department_name_placeholder': '例: 研究開発部',
  'register.department_name_note': '所属する部署名を入力してください',
  'register.password_settings': 'パスワード設定',
  'register.password_label': 'パスワード',
  'register.password_placeholder': 'パスワードを入力',
  'register.password_strength_label': '強度:',
  'register.password_requirements_label': 'パスワード要件:',
  'register.password_requirements_min': '8文字以上',
  'register.password_requirements_cases': '大文字、小文字、数字を含む',
  'register.password_requirements_note':
    'パスワードは8文字以上で、大文字・小文字・数字を含む必要があります。',
  'register.confirm_password_label': 'パスワード（確認）',
  'register.confirm_password_placeholder': 'パスワードを再入力',
  'register.password_match': 'パスワードが一致しています',
  'register.password_mismatch': 'パスワードが一致しません',
  'register.region_settings_title': '言語・タイムゾーン設定',
  'register.language_label': '利用言語',
  'register.language_note': 'システムの表示言語を選択してください。登録後も変更可能です。',
  'register.timezone_label': 'タイムゾーン',
  'register.timezone_note': '日付・時刻の表示に使用されます。登録後も変更可能です。',
  'register.email_already_registered': 'このメールアドレスは既に登録されています',
  'register.email_available': 'このメールアドレスは使用可能です',
  'register.username_already_used': 'このユーザー名は既に使用されています',
  'register.username_available': 'このユーザー名は使用可能です',
  'register.checking_email': '確認中...',
  'register.checking_username': '確認中...',
  'register.required_fields_empty': '必須項目が入力されていません',
  'register.invalid_email': '有効なメールアドレスを入力してください',
  'register.password_min_length_error': 'パスワードは8文字以上である必要があります',
  'register.password_complexity_error': 'パスワードは大文字、小文字、数字を含む必要があります',
  'register.confirm_password_required': 'パスワード確認は必須です',
  'register.password_mismatch_error': 'パスワードが一致しません',
  'register.language_required': '利用言語を選択してください',
  'register.timezone_required': 'タイムゾーンを選択してください',
  'register.terms_required': '利用規約とプライバシーポリシーへの同意が必要です',
  'register.registration_complete_message': '登録が完了しました。ログインしてください。',
  'register.registration_failed_message': '登録に失敗しました',
  'register.password_strength_weak': '弱い',
  'register.password_strength_medium': '普通',
  'register.password_strength_strong': '強い',
  'register.registration_success_title': '登録が完了しました！',
  'register.registration_success_message': 'ログインページにリダイレクトします...',
  'register.terms': '利用規約',
  'register.and': 'および',
  'register.privacy': 'プライバシーポリシー',
  'register.agree': 'に同意します',
  'register.notify_email_label': '研助からのお知らせ（EAの更新など）をメールで受け取る',
  'register.notify_email_note':
    '受け取る種類は、登録後にプロフィールの「お知らせメール」でいつでも変えられます。',
  'register.registering': '登録中...',
  'register.create_account': 'アカウントを作成',
  'register.back_to_login': 'ログインページに戻る',
};

export default function Register() {
  const { t: baseT, i18n, ready } = useTranslation();

  const t = (key: string, options?: Record<string, any>) => {
    if (!key.startsWith('register.')) {
      return baseT(key, options);
    }

    const fallback = options?.defaultValue ?? registerDefaults[key] ?? key;

    // i18n が初期化されていない、または ready でない場合はフォールバックを返す
    if (!ready || !i18n.isInitialized) {
      return fallback;
    }

    // リソースバンドルが読み込まれているか確認
    const hasResource = i18n.hasResourceBundle(i18n.language, 'translation');
    if (!hasResource) {
      return fallback;
    }

    // 翻訳を試みる
    const translated = baseT(key, { ...options, defaultValue: fallback });

    // 翻訳がキーと同じ場合は翻訳が見つからなかったことを意味する
    if (translated === key) {
      return fallback;
    }

    return translated;
  };

  // i18nの言語変更を監視し、formData.languageを更新
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      const validLanguages = ['ja', 'en', 'zh-CN', 'fr', 'es', 'de', 'pt', 'ru'];
      // 言語コードの正規化
      let normalizedLang = lng;
      if (lng.startsWith('zh')) {
        normalizedLang = 'zh-CN';
      }
      if (validLanguages.includes(normalizedLang)) {
        setFormData((prev) => ({ ...prev, language: normalizedLang }));
      }
    };

    // 現在の言語を設定
    const currentLang = i18n.resolvedLanguage || i18n.language;
    if (currentLang) {
      handleLanguageChanged(currentLang);
    }

    // 言語変更イベントをリッスン
    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  // 現在の言語を取得（トップページで選択された言語を反映）
  const getCurrentLanguage = () => {
    const validLanguages = ['ja', 'en', 'zh-CN', 'fr', 'es', 'de', 'pt', 'ru'];
    // i18n.resolvedLanguage または i18n.language を使用
    const currentLang = i18n.resolvedLanguage || i18n.language || 'ja';
    // 言語コードの正規化（例: 'zh' -> 'zh-CN'）
    if (currentLang.startsWith('zh')) {
      return 'zh-CN';
    }
    // 有効な言語リストに含まれているか確認
    if (validLanguages.includes(currentLang)) {
      return currentLang;
    }
    // デフォルトは日本語
    return 'ja';
  };

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    organizationName: '',
    departmentName: '',
    language: getCurrentLanguage(), // トップページで選択された言語をデフォルトとして使用
    timezone: 'Asia/Tokyo', // デフォルトは日本標準時
    agreeTerms: false,
    notifyEmail: true, // お知らせメール（EA更新など）。登録後にプロフィールで種類ごとに変えられる
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [emailError, setEmailError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const navigate = useNavigate();

  // デバウンス用のタイマー
  const emailCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const usernameCheckTimerRef = useRef<NodeJS.Timeout | null>(null);

  // メールアドレスの重複チェック（デバウンス付き）
  const checkEmailDuplicate = async (email: string) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('');
      return;
    }

    setIsCheckingEmail(true);
    try {
      const result = await authService.checkEmailExists(email);
      if (result.data.exists) {
        setEmailError(
          t('register.email_already_registered', {
            defaultValue: 'このメールアドレスは既に登録されています',
          })
        );
      } else {
        setEmailError('');
      }
    } catch (err) {
      // エラー時はチェックをスキップ（サーバーエラーの可能性）
      setEmailError('');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // ユーザー名の重複チェック（デバウンス付き）
  const checkUsernameDuplicate = async (username: string) => {
    if (!username || username.length < 3) {
      setUsernameError('');
      return;
    }

    setIsCheckingUsername(true);
    try {
      const result = await authService.checkUsernameExists(username);
      if (result.data.exists) {
        setUsernameError(
          t('register.username_already_used', {
            defaultValue: 'このユーザー名は既に使用されています',
          })
        );
      } else {
        setUsernameError('');
      }
    } catch (err) {
      // エラー時はチェックをスキップ（サーバーエラーの可能性）
      setUsernameError('');
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value =
      e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
    setError('');

    // パスワード強度チェック
    if (e.target.name === 'password') {
      const password = e.target.value;
      let strength = 0;
      if (password.length >= 8) strength++;
      if (/[a-z]/.test(password)) strength++;
      if (/[A-Z]/.test(password)) strength++;
      if (/\d/.test(password)) strength++;
      if (/[^a-zA-Z\d]/.test(password)) strength++;
      setPasswordStrength(strength);
    }

    // メールアドレスの重複チェック（デバウンス: 500ms）
    if (e.target.name === 'email') {
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current);
      }
      emailCheckTimerRef.current = setTimeout(() => {
        checkEmailDuplicate(value as string);
      }, 500);
    }

    // ユーザー名の重複チェック（デバウンス: 500ms）
    if (e.target.name === 'username' && value) {
      if (usernameCheckTimerRef.current) {
        clearTimeout(usernameCheckTimerRef.current);
      }
      usernameCheckTimerRef.current = setTimeout(() => {
        checkUsernameDuplicate(value as string);
      }, 500);
    }
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError(t('register.required_fields_empty'));
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError(t('register.invalid_email'));
      return false;
    }

    // 重複チェックエラーがある場合は送信を阻止
    if (emailError) {
      setError(emailError);
      return false;
    }

    if (usernameError) {
      setError(usernameError);
      return false;
    }

    if (formData.password.length < 8) {
      setError(t('register.password_min_length_error'));
      return false;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/.test(formData.password)) {
      setError(t('register.password_complexity_error'));
      return false;
    }

    if (!formData.confirmPassword) {
      setError(
        t('register.confirm_password_required', { defaultValue: 'パスワード確認は必須です' })
      );
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.password_mismatch_error'));
      return false;
    }

    if (!formData.language) {
      setError(t('register.language_required'));
      return false;
    }

    if (!formData.timezone) {
      setError(t('register.timezone_required'));
      return false;
    }

    if (!formData.agreeTerms) {
      setError(t('register.terms_required'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setIsRegistering(true);

    try {
      // ユーザー名が未入力の場合はメールアドレスを使用
      const username = formData.username || formData.email;

      await authService.register({
        username,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        language: formData.language,
        timezone: formData.timezone,
        notifyEmail: formData.notifyEmail,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/login', { state: { message: t('register.registration_complete_message') } });
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message;

      // 翻訳キーの形式（例: register.email_already_registered）の場合は翻訳
      if (
        errorMessage &&
        (errorMessage.startsWith('register.') ||
          errorMessage.startsWith('common.') ||
          errorMessage.startsWith('auth.'))
      ) {
        setError(t(errorMessage, { defaultValue: errorMessage }));
      } else {
        setError(errorMessage || t('register.registration_failed_message'));
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 2) return 'danger';
    if (passwordStrength <= 3) return 'warning';
    return 'success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 2) return t('register.password_strength_weak');
    if (passwordStrength <= 3) return t('register.password_strength_medium');
    return t('register.password_strength_strong');
  };

  if (success) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #eff6ff 0%, #f8fafc 55%, #e2e8f0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
        }}
      >
        <Container>
          <Card
            style={{
              maxWidth: '500px',
              margin: '0 auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            }}
          >
            <Card.Body className="text-center p-5">
              <div className="mb-4" style={{ fontSize: '4rem', color: '#28a745' }}>
                <i className="fas fa-check-circle"></i>
              </div>
              <Alert variant="success" className="mb-4">
                <h5 className="mb-2">
                  <i className="fas fa-check-circle me-2"></i>
                  {t('register.registration_success_title')}
                </h5>
                <p className="mb-0">{t('register.registration_success_message')}</p>
              </Alert>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        titleKey="register.title"
        descriptionKey="register.meta_description"
        path="register"
        llmsDescribedBy
        robots="noindex,follow"
      />
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #eff6ff 0%, #f8fafc 55%, #e2e8f0 100%)',
          padding: '2rem 0',
        }}
      >
      <Container>
        <Card
          style={{ maxWidth: '900px', margin: '0 auto', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}
        >
          <Card.Header className="bg-primary text-white text-center py-4">
            <h3 className="mb-0">
              <i className="fas fa-user-plus me-2"></i>
              {t('register.header_title')}
            </h3>
            <p className="mb-0 mt-2" style={{ fontSize: '0.9rem', opacity: 0.9 }}>
              {t('register.header_subtitle')}
            </p>
          </Card.Header>
          <Card.Body className="p-4">
            {/* 使い方の説明 */}
            <CollapsibleHelpSection
              titleKey="register.how_to_use_title"
              descriptionKey="register.how_to_use_desc"
              stepsKey={[
                'register.how_to_use_step1',
                'register.how_to_use_step2',
                'register.how_to_use_step3',
                'register.how_to_use_step4',
                'register.how_to_use_step5',
              ]}
              noteKey="register.password_requirements_note"
              storageKey="register"
            />

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              {/* 基本情報セクション */}
              <div className="mb-4">
                <h5 className="mb-3 text-primary">
                  <i className="fas fa-user me-2"></i>
                  {t('register.basic_info')}
                </h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="register-last-name">
                        {t('register.last_name_label')} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        id="register-last-name"
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        placeholder={t('register.last_name_placeholder')}
                        autoComplete="family-name"
                      />
                      <Form.Text className="text-muted">
                        {t('register.last_name_placeholder')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="register-first-name">
                        {t('register.first_name_label')} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        id="register-first-name"
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        placeholder={t('register.first_name_placeholder')}
                        autoComplete="given-name"
                      />
                      <Form.Text className="text-muted">
                        {t('register.first_name_placeholder')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor="register-email">
                    {t('register.email_label')} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    id="register-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="example@domain.com"
                    autoComplete="email"
                    isInvalid={!!emailError}
                  />
                  {isCheckingEmail && (
                    <Form.Text className="text-muted">
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      {t('register.checking_email', { defaultValue: '確認中...' })}
                    </Form.Text>
                  )}
                  {emailError && (
                    <Form.Control.Feedback type="invalid">
                      <i className="fas fa-exclamation-circle me-1"></i>
                      {emailError}
                    </Form.Control.Feedback>
                  )}
                  {!emailError &&
                    !isCheckingEmail &&
                    formData.email &&
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
                      <Form.Text className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        {t('register.email_available', {
                          defaultValue: 'このメールアドレスは使用可能です',
                        })}
                      </Form.Text>
                    )}
                  {!emailError && !isCheckingEmail && !formData.email && (
                    <Form.Text className="text-muted">{t('register.email_login_note')}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor="register-username">
                    {t('register.username_label')}
                  </Form.Label>
                  <Form.Control
                    id="register-username"
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder={t('register.username_placeholder')}
                    autoComplete="username"
                    isInvalid={!!usernameError}
                  />
                  {isCheckingUsername && formData.username && (
                    <Form.Text className="text-muted">
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      {t('register.checking_username', { defaultValue: '確認中...' })}
                    </Form.Text>
                  )}
                  {usernameError && (
                    <Form.Control.Feedback type="invalid">
                      <i className="fas fa-exclamation-circle me-1"></i>
                      {usernameError}
                    </Form.Control.Feedback>
                  )}
                  {!usernameError &&
                    !isCheckingUsername &&
                    formData.username &&
                    formData.username.length >= 3 && (
                      <Form.Text className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        {t('register.username_available', {
                          defaultValue: 'このユーザー名は使用可能です',
                        })}
                      </Form.Text>
                    )}
                  {!usernameError &&
                    !isCheckingUsername &&
                    (!formData.username || formData.username.length < 3) && (
                      <Form.Text className="text-muted">
                        {t('register.username_placeholder')}
                      </Form.Text>
                    )}
                  {!usernameError && !isCheckingUsername && (
                    <Form.Text
                      className="text-muted"
                      style={{ fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}
                    >
                      {t('register.username_note', {
                        defaultValue:
                          '※ ログインには使用されません。システム内での表示名として使用されます（未入力の場合はメールアドレスが表示名になります）',
                      })}
                    </Form.Text>
                  )}
                </Form.Group>
              </div>

              <hr className="my-4" />

              {/* 組織情報セクション */}
              <div className="mb-4">
                <h5 className="mb-3 text-success">
                  <i className="fas fa-building me-2"></i>
                  {t('register.organization_section')}
                </h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="register-organization-name">
                        {t('register.organization_name')}
                      </Form.Label>
                      <Form.Control
                        id="register-organization-name"
                        type="text"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleChange}
                        placeholder={t('register.organization_name_placeholder')}
                        autoComplete="organization"
                      />
                      <Form.Text className="text-muted">
                        {t('register.organization_name_note')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="register-department-name">
                        {t('register.department_name')}
                      </Form.Label>
                      <Form.Control
                        id="register-department-name"
                        type="text"
                        name="departmentName"
                        value={formData.departmentName}
                        onChange={handleChange}
                        placeholder={t('register.department_name_placeholder')}
                        autoComplete="organization-title"
                      />
                      <Form.Text className="text-muted">
                        {t('register.department_name_note')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <hr className="my-4" />

              {/* パスワード設定セクション */}
              <div className="mb-4">
                <h5 className="mb-3 text-warning">
                  <i className="fas fa-lock me-2"></i>
                  {t('register.password_settings')}
                </h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="register-password">
                        {t('register.password_label')} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        id="register-password"
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength={8}
                        placeholder={t('register.password_placeholder')}
                        autoComplete="new-password"
                      />
                      {formData.password && (
                        <div className="mt-2">
                          <div className="d-flex align-items-center mb-1">
                            <small className="me-2">{t('register.password_strength_label')}</small>
                            <div className="flex-grow-1">
                              <div className="progress" style={{ height: '6px' }}>
                                <div
                                  className={`progress-bar bg-${getPasswordStrengthColor()}`}
                                  role="progressbar"
                                  style={{ width: `${(passwordStrength / 5) * 100}%` }}
                                  aria-valuenow={passwordStrength}
                                  aria-valuemin={0}
                                  aria-valuemax={5}
                                />
                              </div>
                            </div>
                            <small className={`text-${getPasswordStrengthColor()} ms-2 fw-bold`}>
                              {getPasswordStrengthText()}
                            </small>
                          </div>
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        <strong>{t('register.password_requirements_label')}</strong>
                        <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                          <li>{t('register.password_requirements_min')}</li>
                          <li>{t('register.password_requirements_cases')}</li>
                        </ul>
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="register-confirm-password">
                        {t('register.confirm_password_label')}{' '}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        id="register-confirm-password"
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        placeholder={t('register.confirm_password_placeholder')}
                        autoComplete="new-password"
                      />
                      {formData.confirmPassword &&
                        formData.password === formData.confirmPassword && (
                          <Form.Text className="text-success">
                            <i className="fas fa-check-circle me-1"></i>
                            {t('register.password_match')}
                          </Form.Text>
                        )}
                      {formData.confirmPassword &&
                        formData.password !== formData.confirmPassword && (
                          <Form.Text className="text-danger">
                            <i className="fas fa-times-circle me-1"></i>
                            {t('register.password_mismatch')}
                          </Form.Text>
                        )}
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <hr className="my-4" />

              {/* 言語・タイムゾーン設定セクション */}
              <div className="mb-4">
                <h5 className="mb-3 text-info">
                  <i className="fas fa-globe me-2"></i>
                  {t('register.region_settings_title')} <span className="text-danger">*</span>
                </h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="register-language">
                        {t('register.language_label')} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        id="register-language"
                        name="language"
                        value={formData.language}
                        onChange={handleChange}
                        required
                      >
                        {getLanguages(t).map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.nativeName} ({lang.name})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">{t('register.language_note')}</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="register-timezone">
                        {t('register.timezone_label')} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        id="register-timezone"
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleChange}
                        required
                      >
                        {timezoneKeys.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {t(tz.key, { defaultValue: tz.value })}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">{t('register.timezone_note')}</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* お知らせメール */}
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="notifyEmail"
                  name="notifyEmail"
                  checked={formData.notifyEmail}
                  onChange={handleChange}
                  label={t('register.notify_email_label', {
                    defaultValue: '研助からのお知らせ（EAの更新など）をメールで受け取る',
                  })}
                />
                <Form.Text className="text-muted">
                  {t('register.notify_email_note', {
                    defaultValue: '受け取る種類は、登録後にプロフィールの「お知らせメール」でいつでも変えられます。',
                  })}
                </Form.Text>
              </Form.Group>

              {/* 利用規約同意 */}
              <Form.Group className="mb-4">
                <Form.Check
                  type="checkbox"
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  required
                  label={
                    <span>
                      <Link to="/terms" target="_blank" className="text-decoration-none">
                        {t('register.terms')}
                      </Link>
                      {t('register.and')}
                      <Link to="/privacy" target="_blank" className="text-decoration-none">
                        {t('register.privacy')}
                      </Link>
                      {t('register.agree')} <span className="text-danger">*</span>
                    </span>
                  }
                />
              </Form.Group>

              {/* 登録ボタン */}
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  type="submit"
                  size="lg"
                  disabled={isRegistering}
                  className="py-3"
                >
                  {isRegistering ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t('register.registering')}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-plus me-2"></i>
                      {t('register.create_account')}
                    </>
                  )}
                </Button>
                <Link to="/login" className="btn btn-outline-secondary">
                  <i className="fas fa-arrow-left me-2"></i>
                  {t('register.back_to_login')}
                </Link>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
    </>
  );
}
